import React, { useEffect, useMemo, useState } from 'react';
import { Users, GraduationCap, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import './Dashboard.css';
import { useAuth } from '../../AuthContext';
import DashboardCalendar from './DashboardCalendar';
import ClassPerformanceCard from './ClassPerformanceCard';
import ClassListCard from './ClassListCard';
import configData from '../../configure';
import LoadingScreen from '../../components/Common/LoadingScreen';
import FeedbackFab from '../../components/FeedbackFab';
import AdminAssistant from './AdminAssistant';
import { fetchMailboxItems } from '../../services/resourceService';
import { stripHtml } from '../../utils/textUtils';
import {
  AdminStatsGrid,
  CustomOrgStatsGrid,
  AdminFeeSummaryCard,
  AdminAttendanceCard,
  AdminCommunicationCard,
} from './admin/AdminDashboardSections';

const calculateAdminFeeSummary = async (axiosInstance, targetOrgId, targetStudentCount, preloadedClasses = []) => {
  const TARGET_YEAR = '2025-2026';
  try {
    let classes = Array.isArray(preloadedClasses) && preloadedClasses.length > 0 ? preloadedClasses : [];
    if (classes.length === 0) {
      try {
        const clsRes = await axiosInstance.get(`${configData.GET_CLASSES_BY_ORG}/${targetOrgId}/metadata`, {
          headers: { accept: 'application/json' },
        });
        classes = Array.isArray(clsRes.data) ? clsRes.data : clsRes.data?.items || [];
      } catch {
        try {
          const fbRes = await axiosInstance.get(`${configData.GET_CLASSES_BY_ORG}/${targetOrgId}`, {
            headers: { accept: 'application/json' },
          });
          classes = Array.isArray(fbRes.data) ? fbRes.data : fbRes.data?.items || [];
        } catch {
          classes = [];
        }
      }
    }

    if (classes.length === 0) {
      return { collected: 0, pending: 0, rate: '0.0', totalExpected: 0, payments: [], installmentMap: {}, effectiveStudentCount: 0, loading: false };
    }

    const classIds = [...new Set(classes.map((c) => c.classId || c.id).filter(Boolean))];
    const sampleClasses = classIds.slice(0, 15);

    const feeStructures = await Promise.all(
      sampleClasses.map(async (cId) => {
        try {
          const res = await axiosInstance.get(
            `${configData.GET_FEE_STRUCTURE}/class/${cId}?academicYear=${TARGET_YEAR}`,
            { headers: { accept: 'application/hal+json' } }
          );
          const data = Array.isArray(res.data) ? res.data : [res.data];
          return data.find((i) => i?.academicYear === TARGET_YEAR) || data[0] || null;
        } catch {
          return null;
        }
      })
    );

    const validStructures = feeStructures.filter(Boolean);
    if (validStructures.length === 0) {
      return { collected: 0, pending: 0, rate: '0.0', totalExpected: 0, payments: [], installmentMap: {}, effectiveStudentCount: 0, loading: false };
    }

    const installmentIds = new Set();
    const installmentMap = {};
    let totalAnnualFeeSample = 0;

    validStructures.forEach((fs) => {
      const rawInsts = fs.feeInstallments || fs.installments || [];
      const terms = rawInsts.filter((i) => !i.isAnnual && !i.description?.toLowerCase().includes('total annual'));
      let classTotal = 0;
      terms.forEach((t) => {
        const tId = t.installmentId || t.id;
        if (tId) {
          installmentIds.add(tId);
          const amount = t.feeBreakdown
            ? Object.values(t.feeBreakdown).reduce((s, v) => s + (Number(v) || 0), 0)
            : Number(t.totalFee || t.totalAmount || t.amount || 0);
          classTotal += amount;
          installmentMap[tId] = {
            dueDate: t.dueDate || t.due_date || null,
            amount,
            description: t.description || '',
          };
        }
      });
      totalAnnualFeeSample += classTotal;
    });

    const avgFeePerStudent =
      validStructures.length > 0 ? Math.round(totalAnnualFeeSample / validStructures.length) : 0;

    const instIdArray = Array.from(installmentIds);
    const allSuccessfulPayments = [];

    await Promise.all(
      instIdArray.map(async (instId) => {
        try {
          const pRes = await axiosInstance.get(
            `${configData.INFRASTRUCTURE_FEEPAYMENT}/installment/${instId}`,
            { headers: { accept: 'application/hal+json' } }
          );
          const payments = Array.isArray(pRes.data) ? pRes.data : pRes.data?.items || [];
          payments.forEach((p) => {
            if (['SUCCESS', 'COMPLETED', 'PAID'].includes(String(p?.paymentStatus || p?.status || '').toUpperCase())) {
              allSuccessfulPayments.push({
                ...p,
                installmentId: instId,
                installmentDueDate: installmentMap[instId]?.dueDate || null,
                installmentAmount: installmentMap[instId]?.amount || 0,
              });
            }
          });
        } catch {
          // ignore error for single installment
        }
      })
    );

    const totalCollected = allSuccessfulPayments.reduce(
      (sum, p) => sum + Number(p.amountPaid || p.amount || 0),
      0
    );
    const effectiveStudentCount =
      Number(targetStudentCount) > 0 ? Number(targetStudentCount) : validStructures.length * 25;
    const totalExpected = Math.max(totalCollected, effectiveStudentCount * avgFeePerStudent);
    const pending = Math.max(0, totalExpected - totalCollected);
    const rate =
      totalExpected > 0
        ? Math.min(100, Math.max(0, (totalCollected / totalExpected) * 100))
        : totalCollected > 0
        ? 100
        : 0;

    return {
      collected: totalCollected,
      pending,
      rate: rate.toFixed(1),
      totalExpected,
      payments: allSuccessfulPayments,
      installmentMap,
      effectiveStudentCount,
      loading: false,
    };
  } catch (err) {
    console.error('Error calculating admin fee summary:', err);
    return {
      collected: 0,
      pending: 0,
      rate: '0.0',
      totalExpected: 0,
      payments: [],
      installmentMap: {},
      effectiveStudentCount: 0,
      loading: false,
    };
  }
};

function Dashboard({ orgId: orgIdFromFlow }) {
  const navigate = useNavigate();
  const { axios, token, user, orgId: contextOrgId, parentOrgId } = useAuth();
  const orgId = orgIdFromFlow || contextOrgId || user?.orgId || user?.organizationId || user?.org_id || null;
  const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || '';
  const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';

  const [statsCards, setStatsCards] = useState([
    { title: 'Total Students', value: '0', icon: Users, route: '/Students', className: 'students' },
    { title: 'Total Teachers', value: '0', icon: GraduationCap, route: '/Faculty', className: 'teachers' },
    { title: 'Total Staffs', value: '0', icon: Users, route: '/Staff', className: 'staffs' },
    { title: 'Total Courses', value: '0', icon: BookOpen, route: '/courses', className: 'courses' },
  ]);
  const [pieData, setPieData] = useState([
    { name: 'Present', value: 0, color: '#3b82f6' },
    { name: 'Absent', value: 0, color: '#e5e7eb' },
    { name: 'Emergency', value: 0, color: '#f97316' },
    { name: 'Late', value: 0, color: '#f59e0b' },
  ]);
  const [feeSummary, setFeeSummary] = useState({
    collected: 0,
    pending: 0,
    rate: '0.0',
    totalExpected: 0,
    payments: [],
    installmentMap: {},
    effectiveStudentCount: 0,
    loading: true,
  });
  const [todaySummary, setTodaySummary] = useState(null);
  const [customOrgCounts, setCustomOrgCounts] = useState({
    kvsCount: 0,
    nirvahakCount: 0,
    paryavekshakCount: 0,
    samanvaykartaCount: 0,
    studentCount: 0
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const { data: inboxData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['adminDashboardMessages', user?.userId, orgId],
    queryFn: () => fetchMailboxItems({ userId: user?.userId, orgId, size: 4 }),
    enabled: !!user?.userId && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const dashboardMessages = useMemo(() => {
    if (!inboxData?.items) return [];
    return inboxData.items.map((item) => {
      const rawName = item.senderName || item.senderId || 'Notification';
      const name = stripHtml(rawName) || 'Notification';
      const initials = name.split(' ').map((part) => part[0]).join('').toUpperCase().substring(0, 2) || 'N';
      const colors = ['purple', 'green', 'blue', 'orange'];
      const rawText = item.lastMessagePreview || item.subject || item.body || item.content || 'Message';
      const cleanMessage = stripHtml(rawText);
      return {
        name,
        message: cleanMessage || 'New Notification',
        time: item.lastMessageAt || item.createdAt
          ? new Date(item.lastMessageAt || item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'Just now',
        avatar: initials,
        className: colors[Math.abs(name.length % colors.length)],
      };
    });
  }, [inboxData]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const today = new Date().toISOString().split('T')[0];
        const attendanceUrl = configData.get_resources_resources_dashboard_attendance_summary_attendancedate(orgId, today);

        const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

        const promises = [
          axios.get(configData.getCountByUserType(orgId), { headers: { accept: 'application/json' } })
            .catch(err => {
              console.error("Error fetching user type counts:", err);
              return { data: {} };
            }),
          axios.get(configData.getCourseCount(orgId), { headers: { accept: 'application/json' } })
            .catch(err => {
              console.error("Error fetching course count:", err);
              return { data: 0 };
            }),
          axios.get(attendanceUrl, { headers: { accept: 'application/json' } })
            .catch(err => {
              console.error("Error fetching attendance summary:", err);
              return { data: { totalByStatus: {} } };
            }),
        ];

        if (isCustomOrg) {
          promises.push(
            axios.get(configData.getFacultyCountByType(orgId), { headers: { accept: 'application/json' } })
              .catch(err => {
                console.error("Error fetching custom org faculty designation counts:", err);
                return { data: {} };
              }),
            axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, { headers: { accept: 'application/json' } })
              .catch(err => {
                console.error("Error fetching custom org classes count:", err);
                return { data: [] };
              })
          );
        }

        const responses = await Promise.all(promises);
        const userTypeResponse = responses[0];
        const courseCountResponse = responses[1];
        const attendanceResponse = responses[2];

        const userData = userTypeResponse.data || {};
        const getCount = (key) => {
          const upperKey = key.toUpperCase();
          const lowerKey = key.toLowerCase();
          return userData[upperKey] || userData[lowerKey] || userData[key] || 0;
        };

        const studentCount = getCount('STUDENT');
        const facultyCount = getCount('FACULTY') || getCount('TEACHER');
        const staffCount = getCount('STAFF');
        const adminCount = getCount('ADMIN');

        setStatsCards((prev) => [
          { ...prev[0], value: studentCount.toString() },
          { ...prev[1], value: facultyCount.toString() },
          { ...prev[2], value: (staffCount + adminCount).toString() },
          { ...prev[3], value: (courseCountResponse?.data ?? 0).toString() },
        ]);

        let classesData = [];
        if (isCustomOrg) {
          const facultyTypesData = responses[3]?.data || {};
          classesData = responses[4]?.data || [];

          // Aggregate unique classNames (Classes count, removing section variations)
          const uniqueClasses = new Set();
          classesData.forEach(cls => {
            const className = (cls.className || cls.name || '').trim();
            if (className) {
              uniqueClasses.add(className);
            }
          });
          const kvsCount = uniqueClasses.size > 0 ? uniqueClasses.size : classesData.length;

          setCustomOrgCounts({
            kvsCount,
            nirvahakCount: facultyTypesData.NIRVAHAK || 0,
            paryavekshakCount: facultyTypesData.PARYAVEKSHAK || 0,
            samanvaykartaCount: facultyTypesData.SAMANVAYKARTA || 0,
            studentCount
          });
        }

        const totalByStatus = attendanceResponse.data?.totalByStatus || {};
        setTodaySummary(attendanceResponse.data);
        setPieData([
          { name: 'Present', value: totalByStatus.PRESENT ?? 0, color: '#3b82f6' },
          { name: 'Absent', value: totalByStatus.ABSENT ?? 0, color: '#e5e7eb' },
          { name: 'Emergency', value: totalByStatus.EMERGENCY ?? 0, color: '#f97316' },
          { name: 'Late', value: totalByStatus.LATE ?? 0, color: '#f59e0b' },
        ]);

        try {
          const feeData = await calculateAdminFeeSummary(
            axios,
            orgId,
            studentCount,
            isCustomOrg ? classesData : []
          );
          setFeeSummary(feeData);
        } catch (feeError) {
          console.error('Fee summary aggregation failed:', feeError);
          setFeeSummary({
            collected: 0,
            pending: 0,
            rate: '0.0',
            totalExpected: 0,
            payments: [],
            installmentMap: {},
            effectiveStudentCount: 0,
            loading: false,
          });
        }
      } catch (fetchError) {
        setError('Failed to load dashboard data. Please try again later.');
        console.error('Error fetching dashboard data:', fetchError);
      } finally {
        setLoading(false);
      }
    };

    if (orgId) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [axios, orgId]);

  const getFullName = () => {
    if (!user) return 'N/A';
    return `${user.firstName || ''} ${user.middleName || ''} ${user.lastName || ''}`.trim();
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    const value = phone.toString();
    return value.length === 10 ? `+91 ${value.slice(0, 5)} ${value.slice(5)}` : value;
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
  if (!isCustomOrg && loading && statsCards[0].value === '0') {
    return <LoadingScreen title="Syncing Institution Data" subtitle="Aggregating metrics and attendance reports..." />;
  }

  return (
    <div className="dashboard-container" style={{ position: 'relative', minHeight: '100vh' }}>
      {error && <div style={{ color: 'red', padding: '1rem', background: '#fee', borderRadius: '8px', margin: '1rem' }}>{error}</div>}

      {orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83' ? (
        <CustomOrgStatsGrid counts={customOrgCounts} loading={loading} onNavigate={navigate} />
      ) : (
        <AdminStatsGrid
          user={user}
          statsCards={isSmallOrg ? statsCards.filter(c => c.className !== 'staffs') : statsCards}
          loading={loading}
          onNavigate={navigate}
          getFullName={getFullName}
          formatPhoneNumber={formatPhoneNumber}
        />
      )}

      <div className="main-content-grid">
        <div className="dashboard-column dashboard-column-left">
          {isSmallOrg ? (
            <ClassListCard orgId={orgId} />
          ) : (
            <ClassPerformanceCard orgId={orgId} />
          )}

          <AdminFeeSummaryCard
            feeSummary={feeSummary}
            formatCurrency={formatCurrency}
            onViewReports={() => navigate(isSmallOrg ? '/finance' : '/student-payments')}
          />
        </div>

        <div className="dashboard-column dashboard-column-middle">
          <AdminAttendanceCard
            orgId={orgId}
            axios={axios}
            token={token}
            todaySummary={todaySummary}
            onNavigate={navigate}
          />
          <AdminCommunicationCard
            isLoadingMessages={isLoadingMessages}
            messages={dashboardMessages}
            onOpenInbox={() => navigate('/erp-inbox')}
          />
        </div>

        <div className="dashboard-column dashboard-column-right">
          <DashboardCalendar orgId={orgId} />
        </div>
      </div>

      <FeedbackFab />
      <AdminAssistant
        dashboardStats={{
          students: (statsCards || []).find((card) => card?.title === 'Total Students')?.value,
          faculty: (statsCards || []).find((card) => card?.title === 'Total Teachers')?.value,
          staff: (statsCards || []).find((card) => card?.title === 'Total Staffs')?.value,
          courses: (statsCards || []).find((card) => card?.title === 'Total Courses')?.value,
          attendanceRate: ((((pieData || []).find((item) => item?.name === 'Present')?.value || 0) / ((pieData || []).reduce((sum, item) => sum + (item?.value || 0), 0) || 1)) * 100).toFixed(1),
        }}
      />
    </div>
  );
}

export default Dashboard;
